import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  Button,
  Text,
  Box,
  HStack,
  Badge,
  Spinner,
  IconButton,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  UnorderedList,
  ListItem,
  Divider,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Card,
  CardHeader,
  CardBody,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import AudioPlayer from "./AudioPlayer";
import { BASE_URLS, tableName, getBackendURL } from "../../env";
import { getSupabase } from "../../supabaseClient";
import { formatAiRecapBullet } from "../../../utils/aiRecapDisplay";
import { recordingLabel } from "../../../utils/recordingLabel";
import {
  matchTranscriptionToAudio,
  transcriptBelongsToAudio,
  transcriptShowsAsLive,
} from "../../../utils/audioMetadata";
import { clinicalTabValueToPlain } from "../../../utils/clinicalTabFormatting";
import HelpDeskButton from "../../HelpDeskButton";
import CopilotUpgradeButtons from "../../CopilotUpgradeButtons";
import {
  copilotResolvedEhrHint,
  extractTranscriptPlainTextForClinicalAi,
  fetchCopilotSnapshotForAudioPublicUrl,
  fetchCopilotSnapshotAtBucketRelativePath,
  generateClinicalTabsAndPersistCopilotSnapshot,
} from "../../../pages/AudioRecorder/utils/copilotBackupUtils";
import AudioActionItemsPanel from "../../clinic/AudioActionItemsPanel";

/** Preserves newlines and speaker lines; array segments joined with newline, not spaces. */
const formatTranscriptForDisplay = (transcriptField) => {
  if (transcriptField == null || transcriptField === "") return "";
  if (Array.isArray(transcriptField)) {
    return transcriptField
      .map((line) => {
        if (typeof line === "string") return line;
        if (line && typeof line === "object") {
          return line.transcript || line.text || "";
        }
        return "";
      })
      .filter(Boolean)
      .join("\n");
  }
  if (typeof transcriptField === "object") {
    return transcriptField.transcript || transcriptField.text || "";
  }
  return String(transcriptField);
};

/** Scrollable panel for a single Copilot sub-tab in playback modal */
const CopilotSectionPanel = ({ children }) => (
  <Box
    maxH="min(52vh, 460px)"
    overflowY="auto"
    p={3}
    bg="gray.50"
    borderRadius="md"
    borderWidth="1px"
    borderColor="gray.100"
  >
    {children}
  </Box>
);

/** Label for transport and lists: "Recording #n" (" - Mobile" for mobile uploads). */
export function pickAudioDisplayName(audioRow, listIndexOneBased) {
  return recordingLabel(audioRow, listIndexOneBased);
}

const AudioPlayerModal = ({
  isOpen,
  onClose,
  audio,
  allAudios,
  transcripts = [],
  onTranscribeWithCorti,
  onDeleteTranscript,
  onDeleteAudio,
  isTranscribing = false,
}) => {
  const [selectedAudio, setSelectedAudio] = useState(
    audio || (allAudios && allAudios.length > 0 ? allAudios[0] : null),
  );
  const [localRecaps, setLocalRecaps] = useState({}); // transcriptId → recap
  const [generatingRecapFor, setGeneratingRecapFor] = useState(null); // transcriptId
  const [recapErrors, setRecapErrors] = useState({}); // transcriptId → error msg
  const autoRecapAttemptedRef = useRef(new Set()); // transcriptIds attempted in this modal session
  const toast = useToast();
  const [copilotPayload, setCopilotPayload] = useState(null);
  const [copilotLoading, setCopilotLoading] = useState(false);
  /** null = loading current user's flag; only fetch/display snapshot when true */
  const [userCopilotEnabled, setUserCopilotEnabled] = useState(null);
  const [copilotGenerateLoading, setCopilotGenerateLoading] = useState(false);

  const playbackAudioForCopilot = selectedAudio || audio;
  useEffect(() => {
    if (audio && !allAudios) {
      setSelectedAudio(audio);
    }
  }, [audio, allAudios]);

  useEffect(() => {
    if (!isOpen) {
      setUserCopilotEnabled(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const supabase = await getSupabase();
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user || cancelled) {
          if (!cancelled) setUserCopilotEnabled(true);
          return;
        }
        const { data: userData } = await supabase
          .from("users")
          .select("copilot_enabled")
          .eq("id", user.id)
          .single();
        if (!cancelled) {
          setUserCopilotEnabled(userData?.copilot_enabled === true);
        }
      } catch {
        if (!cancelled) setUserCopilotEnabled(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (
      !isOpen ||
      !(playbackAudioForCopilot?.url || playbackAudioForCopilot?.audio_url) ||
      userCopilotEnabled !== true
    ) {
      setCopilotPayload(null);
      setCopilotLoading(false);
      return;
    }
    const pool =
      Array.isArray(transcripts) && transcripts.length > 0
        ? transcripts.filter((t) => !t.is_ai_recap)
        : [];
    const matched =
      playbackAudioForCopilot && pool.length > 0
        ? matchTranscriptionToAudio(playbackAudioForCopilot, pool)
        : null;
    const resolvedCopilotEhr = copilotResolvedEhrHint(
      matched,
      playbackAudioForCopilot,
    );

    let cancelled = false;
    let isAutogenerating = false;
    setCopilotLoading(true);
    setCopilotPayload(null);
    fetchCopilotSnapshotForAudioPublicUrl(
      playbackAudioForCopilot.url || playbackAudioForCopilot.audio_url,
      {
        resolvedCopilotEhr,
      },
    )
      .then((json) => {
        if (!cancelled) {
          if (json) {
            setCopilotPayload(json);
          } else {
            // Fallback: if there is no copilot tabs snapshot, but we have a transcript, generate it!
            const text = extractTranscriptPlainTextForClinicalAi(matched);
            if (text && text.trim()) {
              isAutogenerating = true;
              console.log("ℹ️ [Copilot] Fallback auto-generation triggered.");
              const baseUrl = getBackendURL();
              const audioPublicUrl = playbackAudioForCopilot.url || playbackAudioForCopilot.audio_url;
              const idx = Array.isArray(allAudios) ? allAudios.findIndex(x => (x.url === audioPublicUrl || x.audio_url === audioPublicUrl)) : -1;
              const audioListIndexOneBased = idx >= 0 ? idx + 1 : undefined;

              getSupabase().then(async (supabase) => {
                try {
                  const { data: { session } } = await supabase.auth.getSession();
                  const userId = session?.user?.id;
                  const userJwtToken = session?.access_token;
                  
                  const result = await generateClinicalTabsAndPersistCopilotSnapshot({
                    transcriptText: text,
                    backendBaseUrl: baseUrl,
                    audioRow: playbackAudioForCopilot,
                    transcriptRow: matched,
                    audioPublicUrl,
                    audioListIndexOneBased,
                    userId,
                    userJwtToken,
                  });
                  
                  if (!cancelled && result.ok) {
                    console.log("✅ [Copilot] Fallback auto-generation successful:", result);
                    const ehrHint = copilotResolvedEhrHint(matched, playbackAudioForCopilot);
                    let refreshed = null;
                    if (result.copilotRelativePath) {
                      const originMatch = audioPublicUrl.match(/^https?:\/\/[^/?#]+/i);
                      const storageOrigin = originMatch ? originMatch[0] : "";
                      refreshed = await fetchCopilotSnapshotAtBucketRelativePath(
                        result.copilotRelativePath,
                        storageOrigin
                      );
                    }
                    if (!refreshed) {
                      refreshed = await fetchCopilotSnapshotForAudioPublicUrl(audioPublicUrl, {
                        resolvedCopilotEhr: ehrHint,
                      });
                    }
                    if (!cancelled) {
                      if (result.clinical_tabs) {
                        setCopilotPayload({
                          ...(refreshed && typeof refreshed === "object" ? refreshed : {}),
                          created_at: new Date().toISOString(),
                          clinical_tabs: result.clinical_tabs,
                        });
                      } else if (refreshed) {
                        setCopilotPayload(refreshed);
                      }
                      window.dispatchEvent(new CustomEvent("copilotTabsGenerated"));
                    }
                  }
                } catch (err) {
                  console.error("❌ [Copilot] Fallback auto-generation error:", err);
                } finally {
                  if (!cancelled) {
                    setCopilotLoading(false);
                  }
                }
              }).catch(() => {
                if (!cancelled) {
                  setCopilotLoading(false);
                }
              });
            }
          }
        }
      })
      .finally(() => {
        if (!cancelled && !isAutogenerating) {
          setCopilotLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    playbackAudioForCopilot?.url,
    playbackAudioForCopilot?.audio_url,
    userCopilotEnabled,
    transcripts,
  ]);

  const handleGenerateRecap = useCallback(
    async (t, options = {}) => {
      const { showToast = false } = options;
      const text = formatTranscriptForDisplay(t.transcript).replace(/\n/g, " ");
      if (!text.trim()) {
        if (showToast) {
          toast({
            title: "No transcript text",
            description:
              "Add or save a transcript before generating an AI summary.",
            status: "warning",
            duration: 4000,
            isClosable: true,
          });
        }
        return;
      }
      setGeneratingRecapFor(t.id);
      setRecapErrors((prev) => {
        const n = { ...prev };
        delete n[t.id];
        return n;
      });
      try {
        const supabase = await getSupabase();
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        const userJwtToken = session?.access_token;
        const schema = t?.schema_name || t?.metadata?.schema_name || "public";
        const patientId = t?.patient_id || "unknown";
        const sessionId = t?.session_id || `session_${patientId}`;

        const hostname = window.location.hostname;
        const baseUrl =
          hostname === "localhost"
            ? BASE_URLS.local.backend
            : BASE_URLS.render.backend;
        const res = await fetch(`${baseUrl}/api/generate-ai-recap/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            transcript: text,
            userId,
            userJwtToken,
            patientId,
            sessionId,
            schema,
          }),
        });
        const json = await res.json();
        if (!res.ok || json.error)
          throw new Error(json.error || "Failed to generate recap");
        const recap = json.recap;
        const recapHasContent =
          recap &&
          typeof recap === "object" &&
          ((typeof recap.summary === "string" && recap.summary.trim()) ||
            (Array.isArray(recap.key_takeaways) &&
              recap.key_takeaways.some((x) =>
                typeof x === "string" ? x.trim() : x,
              )) ||
            (Array.isArray(recap.action_items) &&
              recap.action_items.some((x) =>
                typeof x === "string" ? x.trim() : x,
              )));
        if (!recapHasContent) {
          console.warn(
            "[AI recap] backend returned empty recap. Full response:",
            json,
          );
          const otherKeys =
            recap && typeof recap === "object"
              ? Object.keys(recap)
              : Object.keys(json || {});
          throw new Error(
            `Backend returned an empty recap (likely API credits exhausted, model truncation, or unexpected response shape). Response keys: ${
              otherKeys.length ? otherKeys.join(", ") : "none"
            }. See console for full response.`,
          );
        }
        // Show immediately in UI
        setLocalRecaps((prev) => ({ ...prev, [t.id]: recap }));
        // Persist to DB (same logic as Schedules saved transcription)
        if (t.patient_id) {
          try {
            const supabase = await getSupabase();
            const { data: patientData, error: fetchErr } = await supabase
              .from(tableName)
              .select("transcripts")
              .eq("id", t.patient_id)
              .single();
            if (fetchErr) throw fetchErr;

            const list = Array.isArray(patientData?.transcripts)
              ? [...patientData.transcripts]
              : [];
            const existingRecap = list.find(
              (tx) => tx.is_ai_recap && tx.linked_transcript_id === t.id,
            );

            if (existingRecap) {
              const updated = list.map((tx) =>
                tx.id === existingRecap.id
                  ? {
                      ...tx,
                      summary: recap.summary || "",
                      key_takeaways: recap.key_takeaways || [],
                      action_items: recap.action_items || [],
                      created_at: new Date().toISOString(),
                    }
                  : tx,
              );
              const { error: upErr } = await supabase
                .from(tableName)
                .update({ transcripts: updated })
                .eq("id", t.patient_id);
              if (upErr) throw upErr;
            } else {
              const recapEntry = {
                id: crypto.randomUUID
                  ? crypto.randomUUID()
                  : `${Date.now()}-${Math.random()}`,
                is_ai_recap: true,
                linked_transcript_id: t.id,
                patient_id: String(t.patient_id),
                summary: recap.summary || "",
                key_takeaways: recap.key_takeaways || [],
                action_items: recap.action_items || [],
                created_at: new Date().toISOString(),
              };
              const { error: upErr } = await supabase
                .from(tableName)
                .update({ transcripts: [...list, recapEntry] })
                .eq("id", t.patient_id);
              if (upErr) throw upErr;
            }

            if (showToast) {
              toast({
                title: "AI summary saved",
                description: "Summary is stored with this transcript.",
                status: "success",
                duration: 3000,
                isClosable: true,
              });
            }
          } catch (dbErr) {
            console.warn(
              "⚠️ Could not save recap to transcript:",
              dbErr.message,
            );
            if (showToast) {
              toast({
                title: "Save failed",
                description:
                  dbErr.message ||
                  "Summary was generated but could not be saved to the patient record.",
                status: "error",
                duration: 5000,
                isClosable: true,
              });
            }
          }
        }
      } catch (err) {
        setRecapErrors((prev) => ({
          ...prev,
          [t.id]: err.message || "Failed to generate recap.",
        }));
        if (showToast) {
          toast({
            title: "AI summary failed",
            description: err.message || "Failed to generate summary.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      } finally {
        setGeneratingRecapFor(null);
      }
    },
    [toast],
  );

  const handleGenerateCopilotTabs = useCallback(async () => {
    const audioFile = playbackAudioForCopilot;
    const audioPublicUrl = audioFile?.url || audioFile?.audio_url || "";
    if (!audioPublicUrl.trim()) {
      toast({
        title: "No audio URL",
        description: "Select an audio file with a saved storage URL.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    const pool =
      Array.isArray(transcripts) && transcripts.length > 0
        ? transcripts.filter((t) => !t.is_ai_recap)
        : [];
    const matched = matchTranscriptionToAudio(audioFile, pool);
    if (!matched) {
      toast({
        title: "No matching transcript",
        description: "Save a transcript linked to this audio, then try again.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    const text = extractTranscriptPlainTextForClinicalAi(matched);
    if (!text.trim()) {
      toast({
        title: "Transcript is empty",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const hostname = window.location.hostname;
    const baseUrl =
      hostname === "localhost"
        ? BASE_URLS.local.backend
        : BASE_URLS.render.backend;

    const audioListIndexOneBased =
      Array.isArray(allAudios) && allAudios.length > 0
        ? allAudios.findIndex(
            (a) =>
              (a?.url || a?.audio_url || "").split("?")[0] ===
              audioPublicUrl.split("?")[0],
          ) + 1
        : undefined;

    setCopilotGenerateLoading(true);
    try {
      const supabase = await getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      const userJwtToken = session?.access_token;

      const result = await generateClinicalTabsAndPersistCopilotSnapshot({
        transcriptText: text,
        backendBaseUrl: baseUrl,
        audioRow: audioFile,
        transcriptRow: matched,
        audioPublicUrl,
        existingClinicalTabs: copilotPayload?.clinical_tabs ?? null,
        audioListIndexOneBased:
          audioListIndexOneBased >= 1 ? audioListIndexOneBased : undefined,
        userId,
        userJwtToken,
      });
      if (!result.ok) throw new Error(result.error);

      const originMatch = audioPublicUrl.match(/^https?:\/\/[^/?#]+/i);
      const storageOrigin = originMatch ? originMatch[0] : "";
      const ehrHint = copilotResolvedEhrHint(matched, audioFile);

      let refreshed = null;
      if (result.copilotRelativePath) {
        refreshed = await fetchCopilotSnapshotAtBucketRelativePath(
          result.copilotRelativePath,
          storageOrigin,
        );
      }
      if (!refreshed) {
        refreshed = await fetchCopilotSnapshotForAudioPublicUrl(audioPublicUrl, {
          resolvedCopilotEhr: ehrHint,
        });
      }

      if (result.clinical_tabs) {
        // Always carry the metadata the Orders & Followups panel keys off
        // (ehr_id / schema_name / audio_number), even when the post-save storage
        // re-fetch hasn't propagated yet (otherwise the panel renders nothing
        // right after a first-time generate).
        const apSchemaName =
          matched?.schema_name ||
          matched?.metadata?.schema_name ||
          audioFile?.schema_name ||
          audioFile?.metadata?.schema_name ||
          "public";
        const mergedMeta = {
          ...(refreshed?.metadata || {}),
          ...(copilotPayload?.metadata || {}),
          ehr_id:
            copilotPayload?.metadata?.ehr_id ||
            refreshed?.metadata?.ehr_id ||
            ehrHint ||
            "",
          schema_name:
            copilotPayload?.metadata?.schema_name ||
            refreshed?.metadata?.schema_name ||
            apSchemaName,
          patient_name:
            copilotPayload?.metadata?.patient_name ||
            refreshed?.metadata?.patient_name ||
            matched?.patient_name ||
            audioFile?.patient_name ||
            "",
        };
        const fromApi = {
          ...(refreshed && typeof refreshed === "object" ? refreshed : {}),
          ...(copilotPayload && typeof copilotPayload === "object"
            ? copilotPayload
            : {}),
          audio_number:
            refreshed?.audio_number ??
            copilotPayload?.audio_number ??
            (audioListIndexOneBased >= 1 ? audioListIndexOneBased : undefined),
          metadata: mergedMeta,
          created_at: new Date().toISOString(),
          clinical_tabs: result.clinical_tabs,
        };
        setCopilotPayload(fromApi);
        toast({
          title: "Copilot tabs generated",
          description: "Clinical tabs were saved for this recording.",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      } else if (refreshed) {
        setCopilotPayload(refreshed);
        toast({
          title: "Copilot tabs generated",
          description: "Clinical tabs were saved for this recording.",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Saved",
          description:
            "Tabs were written to storage. Switch away from the Copilot tab and back if they do not appear.",
          status: "info",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      toast({
        title: "Could not generate Copilot tabs",
        description: err.message || "Unknown error",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setCopilotGenerateLoading(false);
    }
  }, [playbackAudioForCopilot, allAudios, transcripts, toast, copilotPayload]);

  // Match transcripts to a specific audio file (excludes recap entries).
  // Use session_id and normalized URLs — strict === on audio_url alone breaks restores.
  const getTranscriptsForAudio = (audioFile) => {
    if (!transcripts || !Array.isArray(transcripts) || !audioFile) return [];
    return transcripts.filter(
      (t) => !t.is_ai_recap && transcriptBelongsToAudio(audioFile, t),
    );
  };

  // Find recap entry for a given live transcript; returns {summary, key_takeaways, action_items} shape
  const getRecapForTranscript = (t) => {
    if (!transcripts || !Array.isArray(transcripts)) return null;
    const entry = transcripts.find(
      (r) => r.is_ai_recap && r.linked_transcript_id === t.id,
    );
    if (!entry) return null;
    return {
      summary: entry.summary,
      key_takeaways: entry.key_takeaways,
      action_items: entry.action_items,
    };
  };

  // Auto-generate AI summary when opening playback for new audios that already
  // have transcript text but no recap entry yet.
  useEffect(() => {
    if (!isOpen || !selectedAudio || generatingRecapFor) return;
    if (!Array.isArray(transcripts) || transcripts.length === 0) return;

    const matchedTranscripts = getTranscriptsForAudio(selectedAudio);
    const nextTranscriptMissingRecap = matchedTranscripts.find((t) => {
      const transcriptText = formatTranscriptForDisplay(t.transcript).replace(
        /\n/g,
        " ",
      );
      if (!transcriptText.trim()) return false;

      const hasRecap = !!(getRecapForTranscript(t) || localRecaps[t.id]);
      if (hasRecap) return false;

      return !autoRecapAttemptedRef.current.has(t.id);
    });

    if (nextTranscriptMissingRecap) {
      autoRecapAttemptedRef.current.add(nextTranscriptMissingRecap.id);
      handleGenerateRecap(nextTranscriptMissingRecap);
    }
  }, [
    isOpen,
    selectedAudio,
    transcripts,
    localRecaps,
    generatingRecapFor,
    handleGenerateRecap,
  ]);

  // Render facts section
  const renderFacts = (facts) => {
    if (!facts || !Array.isArray(facts) || facts.length === 0) return null;
    return (
      <Box mt={2}>
        <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
          Extracted Facts ({facts.length}):
        </Text>
        <Box maxH="200px" overflowY="auto" pl={2}>
          {facts.map((fact, i) => (
            <Box key={i} mb={1}>
              <Text fontSize="xs" color="gray.700">
                {i + 1}.{" "}
                {fact.text ||
                  (typeof fact === "string" ? fact : JSON.stringify(fact))}
                {fact.group && (
                  <Badge ml={1} size="sm" colorScheme="gray" fontSize="0.65em">
                    {fact.group}
                  </Badge>
                )}
              </Text>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  // Render SOAP notes section
  const renderSoapNotes = (soap_notes) => {
    if (!soap_notes || Object.keys(soap_notes).length === 0) return null;
    return (
      <Box mt={2}>
        <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>
          SOAP Notes:
        </Text>
        <Box pl={2}>
          {soap_notes.transcript_only && (
            <Box mb={2}>
              <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                Transcript-Based:
              </Text>
              <Box maxH="150px" overflowY="auto">
                <Text fontSize="xs" color="gray.700" whiteSpace="pre-wrap">
                  {soap_notes.transcript_only}
                </Text>
              </Box>
            </Box>
          )}
          {soap_notes.facts_only && (
            <Box mb={2}>
              <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                Facts-Based:
              </Text>
              <Box maxH="150px" overflowY="auto">
                <Text fontSize="xs" color="gray.700" whiteSpace="pre-wrap">
                  {soap_notes.facts_only}
                </Text>
              </Box>
            </Box>
          )}
          {soap_notes.combined && (
            <Box mb={2}>
              <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                Combined:
              </Text>
              <Box maxH="150px" overflowY="auto">
                <Text fontSize="xs" color="gray.700" whiteSpace="pre-wrap">
                  {soap_notes.combined}
                </Text>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  // Render transcript section for a given audio
  const renderTranscriptSection = (audioFile) => {
    const matchedTranscripts = getTranscriptsForAudio(audioFile);
    const hasCorti = matchedTranscripts.some((t) => !transcriptShowsAsLive(t));

    const transcriptHasAiSummary = (t) =>
      !!(getRecapForTranscript(t) || localRecaps[t.id]);
    const summaryBannerCount = matchedTranscripts.filter(
      transcriptHasAiSummary,
    ).length;
    const firstSummaryIdx = matchedTranscripts.findIndex(
      transcriptHasAiSummary,
    );

    return (
      <Box mt={4} w="100%">
        {matchedTranscripts.length > 0 ? (
          <VStack spacing={3} align="stretch">
            <HStack align="baseline" spacing={2} flexWrap="wrap">
              <Text fontSize="md" fontWeight="bold">
                Transcripts:
              </Text>
              {summaryBannerCount > 0 && (
                <Badge colorScheme="purple" fontSize="0.7em">
                  {summaryBannerCount === 1
                    ? "AI summary available"
                    : `${summaryBannerCount} AI summaries`}
                </Badge>
              )}
            </HStack>
            {summaryBannerCount > 0 && (
              <Box
                py={2}
                px={3}
                borderRadius="md"
                bg="purple.50"
                borderWidth="1px"
                borderColor="purple.200"
              >
                <Text fontSize="sm" color="purple.800">
                  {summaryBannerCount === 1
                    ? "An AI summary is saved for this recording. Expand the transcript row below (look for the purple “AI summary” tag) to read it."
                    : "AI summaries are saved for this recording. Expand each transcript row below (purple “AI summary” tags) to read them."}
                </Text>
              </Box>
            )}
            <Accordion
              allowMultiple
              allowToggle
              defaultIndex={
                firstSummaryIdx >= 0 ? [firstSummaryIdx] : undefined
              }
            >
              {matchedTranscripts.map((t, idx) => {
                const transcriptTextForAi = formatTranscriptForDisplay(
                  t.transcript,
                ).replace(/\n/g, " ");
                const hasTranscriptText = transcriptTextForAi.trim().length > 0;
                const hasAiSummarySaved = transcriptHasAiSummary(t);
                return (
                  <AccordionItem
                    key={t.id || idx}
                    border="1px solid"
                    borderColor={
                      transcriptShowsAsLive(t) ? "green.200" : "blue.200"
                    }
                    borderRadius="md"
                    mb={2}
                    bg={transcriptShowsAsLive(t) ? "green.50" : "blue.50"}
                  >
                    <AccordionButton py={2} px={3}>
                      <HStack flex="1" justify="space-between">
                        <HStack spacing={2} flexWrap="wrap">
                          <Badge
                            colorScheme={
                              transcriptShowsAsLive(t) ? "green" : "blue"
                            }
                          >
                            {transcriptShowsAsLive(t)
                              ? "Live Transcript"
                              : "Corti Transcript"}
                          </Badge>
                          {hasAiSummarySaved && (
                            <Badge
                              colorScheme="purple"
                              variant="solid"
                              fontSize="0.65em"
                            >
                              AI summary
                            </Badge>
                          )}
                          {t.facts && t.facts.length > 0 && (
                            <Badge colorScheme="purple" fontSize="0.65em">
                              {t.facts.length} fact(s)
                            </Badge>
                          )}
                          {t.soap_notes &&
                            Object.keys(t.soap_notes).length > 0 && (
                              <Badge colorScheme="orange" fontSize="0.65em">
                                SOAP
                              </Badge>
                            )}
                        </HStack>
                        <Text fontSize="xs" color="gray.500">
                          {t.created_at
                            ? new Date(t.created_at).toLocaleString()
                            : ""}
                        </Text>
                      </HStack>
                      <AccordionIcon ml={2} />
                    </AccordionButton>
                    <AccordionPanel pb={3} px={3}>
                      {/* Transcript text */}
                      <Box>
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          color="gray.700"
                          mb={1}
                        >
                          Transcript:
                        </Text>
                        <Box maxH="200px" overflowY="auto">
                          <Text
                            fontSize="sm"
                            color="gray.700"
                            whiteSpace="pre-wrap"
                            wordBreak="break-word"
                          >
                            {formatTranscriptForDisplay(t.transcript)}
                          </Text>
                        </Box>
                      </Box>

                      {/* AI Summary — same API as recap; any transcript with text */}
                      {hasTranscriptText &&
                        (() => {
                          const recap =
                            getRecapForTranscript(t) || localRecaps[t.id];
                          const isGenerating = generatingRecapFor === t.id;
                          const error = recapErrors[t.id];
                          if (recap) {
                            return (
                              <Box mt={4}>
                                <Divider mb={3} />
                                <HStack spacing={2} mb={2}>
                                  <Text
                                    fontSize="sm"
                                    fontWeight="semibold"
                                    color="purple.700"
                                  >
                                    AI Summary
                                  </Text>
                                  <Badge colorScheme="purple" fontSize="0.6em">
                                    AI
                                  </Badge>
                                </HStack>
                                {recap.summary && (
                                  <Box mb={3}>
                                    <Text
                                      fontSize="xs"
                                      fontWeight="semibold"
                                      color="gray.600"
                                      mb={1}
                                    >
                                      Summary
                                    </Text>
                                    <Text fontSize="sm" color="gray.700">
                                      {recap.summary}
                                    </Text>
                                  </Box>
                                )}
                                {recap.key_takeaways?.length > 0 && (
                                  <Box mb={3}>
                                    <Text
                                      fontSize="xs"
                                      fontWeight="semibold"
                                      color="gray.600"
                                      mb={1}
                                    >
                                      Key Takeaways
                                    </Text>
                                    <UnorderedList spacing={1} pl={2}>
                                      {recap.key_takeaways.map((item, i) => (
                                        <ListItem
                                          key={i}
                                          fontSize="sm"
                                          color="gray.700"
                                        >
                                          {formatAiRecapBullet(item)}
                                        </ListItem>
                                      ))}
                                    </UnorderedList>
                                  </Box>
                                )}
                                {recap.action_items?.length > 0 && (
                                  <Box>
                                    <Text
                                      fontSize="xs"
                                      fontWeight="semibold"
                                      color="gray.600"
                                      mb={1}
                                    >
                                      Action Items
                                    </Text>
                                    <UnorderedList spacing={1} pl={2}>
                                      {recap.action_items.map((item, i) => (
                                        <ListItem
                                          key={i}
                                          fontSize="sm"
                                          color="gray.700"
                                        >
                                          {formatAiRecapBullet(item)}
                                        </ListItem>
                                      ))}
                                    </UnorderedList>
                                  </Box>
                                )}
                                <HStack mt={4} spacing={2}>
                                  <Button
                                    size="sm"
                                    colorScheme="purple"
                                    variant="solid"
                                    onClick={() =>
                                      handleGenerateRecap(t, {
                                        showToast: true,
                                      })
                                    }
                                    isLoading={isGenerating}
                                    loadingText="…"
                                    isDisabled={
                                      generatingRecapFor !== null &&
                                      generatingRecapFor !== t.id
                                    }
                                    title="Regenerate AI summary (same API as audio flow)"
                                  >
                                    Regenerate AI summary
                                  </Button>
                                </HStack>
                              </Box>
                            );
                          }
                          // No recap yet — show generate button (or spinner / error)
                          return (
                            <Box mt={4}>
                              <Divider mb={3} />
                              <HStack spacing={2} mb={2}>
                                <Text
                                  fontSize="sm"
                                  fontWeight="semibold"
                                  color="purple.700"
                                >
                                  AI Summary
                                </Text>
                                <Badge colorScheme="purple" fontSize="0.6em">
                                  AI
                                </Badge>
                              </HStack>
                              {isGenerating ? (
                                <HStack spacing={2}>
                                  <Spinner size="sm" color="purple.500" />
                                  <Text fontSize="sm" color="gray.500">
                                    Generating summary…
                                  </Text>
                                </HStack>
                              ) : error ? (
                                <VStack align="start" spacing={1}>
                                  <Text fontSize="xs" color="red.500">
                                    ❌ {error}
                                  </Text>
                                  <Button
                                    size="xs"
                                    colorScheme="purple"
                                    variant="outline"
                                    onClick={() =>
                                      handleGenerateRecap(t, {
                                        showToast: true,
                                      })
                                    }
                                  >
                                    Retry
                                  </Button>
                                </VStack>
                              ) : (
                                <Button
                                  size="sm"
                                  colorScheme="purple"
                                  variant="outline"
                                  onClick={() =>
                                    handleGenerateRecap(t, {
                                      showToast: true,
                                    })
                                  }
                                  isDisabled={generatingRecapFor !== null}
                                  title="Create AI summary from this transcript"
                                >
                                  Create AI summary
                                </Button>
                              )}
                            </Box>
                          );
                        })()}

                      {/* Facts */}
                      {renderFacts(t.facts)}

                      {/* SOAP Notes */}
                      {renderSoapNotes(t.soap_notes)}

                      {/* Delete button */}
                      {onDeleteTranscript && (
                        <HStack justify="flex-end" mt={3}>
                          <Button
                            size="xs"
                            colorScheme="red"
                            variant="outline"
                            leftIcon={<DeleteIcon />}
                            onClick={() => onDeleteTranscript(t.id)}
                          >
                            Delete Transcript
                          </Button>
                        </HStack>
                      )}
                    </AccordionPanel>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </VStack>
        ) : (
          <Text fontSize="sm" color="gray.500" fontStyle="italic">
            No transcripts available for this audio.
          </Text>
        )}

        {!hasCorti && onTranscribeWithCorti && (
          <Button
            mt={3}
            colorScheme="purple"
            size="sm"
            onClick={() => onTranscribeWithCorti(audioFile)}
            isLoading={isTranscribing}
            loadingText="Transcribing..."
            isDisabled={isTranscribing}
          >
            Transcribe with Corti
          </Button>
        )}
      </Box>
    );
  };

  const renderCopilotStorageTab = () => {
    if (userCopilotEnabled === null) {
      return (
        <HStack spacing={2} py={4}>
          <Spinner size="sm" color="blue.500" />
          <Text fontSize="sm" color="gray.600">
            Loading…
          </Text>
        </HStack>
      );
    }
    if (userCopilotEnabled === false) {
      return (
        <VStack align="stretch" spacing={3} py={4}>
          <Text fontSize="sm" color="gray.700">
            Upgrade to premium to enable Copilot and view saved clinical tabs for
            this recording.
          </Text>
          <CopilotUpgradeButtons alignSelf="flex-start" />
          <Text fontSize="sm" color="gray.600">
            If you need help upgrading your plan, use Help Desk to send a
            message for support.
          </Text>
          <HelpDeskButton size="sm" buttonProps={{ alignSelf: "flex-start" }} />
        </VStack>
      );
    }
    if (copilotLoading || copilotGenerateLoading) {
      return (
        <HStack spacing={2} py={4}>
          <Spinner size="sm" color="blue.500" />
          <Text fontSize="sm" color="gray.600">
            {copilotGenerateLoading ? "Generating Copilot tabs..." : "Loading Copilot snapshot…"}
          </Text>
        </HStack>
      );
    }

    const audioFileForCopilot = playbackAudioForCopilot;
    const transcriptPoolNonRecap =
      Array.isArray(transcripts) && transcripts.length > 0
        ? transcripts.filter((t) => !t.is_ai_recap)
        : [];
    const matchedTranscriptForCopilot =
      audioFileForCopilot && transcriptPoolNonRecap.length > 0
        ? matchTranscriptionToAudio(
            audioFileForCopilot,
            transcriptPoolNonRecap,
          )
        : null;
    const copilotTranscriptTextTrimmed =
      extractTranscriptPlainTextForClinicalAi(matchedTranscriptForCopilot);
    const canGenerateOrRegenerateCopilot = !!copilotTranscriptTextTrimmed;

    if (!copilotPayload) {
      return (
        <VStack align="stretch" spacing={3} py={3}>
          <Text fontSize="sm" color="gray.500" fontStyle="italic">
            No Copilot snapshot found for this recording yet.
          </Text>
          {canGenerateOrRegenerateCopilot ? (
            <Button
              size="sm"
              colorScheme="purple"
              onClick={handleGenerateCopilotTabs}
              isLoading={copilotGenerateLoading}
              loadingText="Generating…"
              alignSelf="flex-start"
              maxW="100%"
              whiteSpace="normal"
              height="auto"
              py={2}
            >
              Generate Copilot tabs from transcript
            </Button>
          ) : (
            <Text fontSize="xs" color="gray.500">
              Link a transcript to this audio (or open playback from a patient
              with transcripts) to generate Copilot tabs here. Recordings made
              with the external Copilot window still save tabs automatically.
            </Text>
          )}
        </VStack>
      );
    }
    const ct = copilotPayload.clinical_tabs;
    const sections = [];

    const pushPlainSection = (id, label, raw, { alwaysShow = false } = {}) => {
      const val = clinicalTabValueToPlain(raw);
      const text =
        val && String(val).trim()
          ? String(val).trim()
          : alwaysShow
            ? "Not discussed"
            : "";
      if (!text) return;

      let contentNode;
      if (id === "consider" && text !== "Not discussed") {
        const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
        let itemsToUse = [];
        lines.forEach(line => {
          if (line.match(/^[\-\*•\d\.]/)) {
            itemsToUse.push(line);
          } else {
            const sentences = line.match(/[^.!?]+[.!?]+(\s|$)/g) || [line];
            sentences.forEach(s => {
              const trimmed = s.trim();
              if (trimmed) itemsToUse.push(trimmed);
            });
          }
        });

        const bulletItems = [];
        itemsToUse.forEach(item => {
          const clean = item.replace(/^[\s\-\*•\d\.\)]+/, '').trim();
          if (clean) {
            bulletItems.push(clean);
          }
        });

        if (bulletItems.length > 0) {
          contentNode = (
            <UnorderedList spacing={2} pl={1}>
              {bulletItems.map((item, i) => (
                <ListItem key={i} fontSize="sm" color="gray.800">
                  {item}
                </ListItem>
              ))}
            </UnorderedList>
          );
        } else {
          contentNode = (
            <Text
              fontSize="sm"
              whiteSpace="pre-wrap"
              wordBreak="break-word"
              color="gray.800"
            >
              {text}
            </Text>
          );
        }
      } else {
        contentNode = (
          <Text
            fontSize="sm"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
            color={text === "Not discussed" ? "gray.500" : "gray.800"}
            fontStyle={text === "Not discussed" ? "italic" : "normal"}
          >
            {text}
          </Text>
        );
      }

      sections.push({
        id,
        label,
        node: (
          <CopilotSectionPanel>
            {contentNode}
          </CopilotSectionPanel>
        ),
      });
    };

    if (ct && typeof ct === "object") {
      const evidence = ct.evidence;
      const evidenceHasContent =
        evidence != null &&
        evidence !== "" &&
        !(Array.isArray(evidence) && evidence.length === 0);

      let assessmentPlain = "";
      if (ct.assessment != null && ct.assessment !== "") {
        const v = clinicalTabValueToPlain(ct.assessment);
        if (v && String(v).trim()) assessmentPlain = String(v);
      }
      if (!assessmentPlain) assessmentPlain = "Not discussed";

      let planPlain = "";
      if (ct.plan != null && ct.plan !== "") {
        const v = clinicalTabValueToPlain(ct.plan);
        if (v && String(v).trim()) planPlain = String(v);
      }
      if (!planPlain) planPlain = "Not discussed";

      sections.push({
        id: "assessment_plan_evidence",
        label: "A&P",
        node: (
          <VStack align="stretch" spacing={4}>
            <Card variant="outline" size="sm" boxShadow="sm" bg="white">
              <CardHeader py={2} pb={1}>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.700"
                  textTransform="uppercase"
                  letterSpacing="wide"
                >
                  Assessment
                </Text>
              </CardHeader>
              <CardBody pt={0} maxH="min(38vh, 280px)" overflowY="auto">
                <Text
                  fontSize="sm"
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                  color={
                    assessmentPlain === "Not discussed"
                      ? "gray.500"
                      : "gray.800"
                  }
                  fontStyle={
                    assessmentPlain === "Not discussed" ? "italic" : "normal"
                  }
                >
                  {assessmentPlain}
                </Text>
              </CardBody>
            </Card>
            <Card variant="outline" size="sm" boxShadow="sm" bg="white">
              <CardHeader py={2} pb={1}>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.700"
                  textTransform="uppercase"
                  letterSpacing="wide"
                >
                  Plan
                </Text>
              </CardHeader>
              <CardBody pt={0} maxH="min(38vh, 280px)" overflowY="auto">
                <Text
                  fontSize="sm"
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                  color={planPlain === "Not discussed" ? "gray.500" : "gray.800"}
                  fontStyle={
                    planPlain === "Not discussed" ? "italic" : "normal"
                  }
                >
                  {planPlain}
                </Text>
              </CardBody>
            </Card>
            <Card variant="outline" size="sm" boxShadow="sm" bg="white">
              <CardHeader py={2} pb={1}>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.700"
                  textTransform="uppercase"
                  letterSpacing="wide"
                >
                  Evidence
                </Text>
              </CardHeader>
              <CardBody pt={0} maxH="min(38vh, 280px)" overflowY="auto">
                {evidenceHasContent ? (
                  Array.isArray(evidence) ? (
                    <UnorderedList spacing={2} pl={1}>
                      {evidence.map((item, i) => (
                        <ListItem key={i} fontSize="sm" color="gray.800">
                          {clinicalTabValueToPlain(item)}
                        </ListItem>
                      ))}
                    </UnorderedList>
                  ) : (
                    <Text
                      fontSize="sm"
                      whiteSpace="pre-wrap"
                      wordBreak="break-word"
                      color="gray.800"
                    >
                      {clinicalTabValueToPlain(evidence)}
                    </Text>
                  )
                ) : (
                  <Text fontSize="sm" color="gray.500" fontStyle="italic">
                    No evidence summary.
                  </Text>
                )}
              </CardBody>
            </Card>
        {(() => {
          const apMeta = (copilotPayload && copilotPayload.metadata) || {};
          const apEhr = apMeta.ehr_id;
          const apAudioNum = copilotPayload ? copilotPayload.audio_number : undefined;
          const apSchema = apMeta.schema_name || "public";
          const apPatient = apMeta.patient_name || "";
          if (!apEhr) return null;
          return (
            <Box>
              <Text fontSize="xs" fontWeight="semibold" color="gray.700" textTransform="uppercase" letterSpacing="wide" mb={2}>
                Orders &amp; Followups
              </Text>
              <AudioActionItemsPanel
                schema={apSchema}
                ehrId={apEhr}
                audioNumber={apAudioNum}
                patientName={apPatient}
              />
            </Box>
          );
        })()}
          </VStack>
        ),
      });

      pushPlainSection("consider", "Consider", ct.consider, { alwaysShow: true });

      const scores = ct.scores;
      const scoresText =
        scores != null && scores !== ""
          ? typeof scores === "string"
            ? scores
            : JSON.stringify(scores, null, 2)
          : "None mentioned";
      sections.push({
        id: "scores",
        label: "Scores",
        node: (
          <CopilotSectionPanel>
            <Box
              as="pre"
              fontSize="xs"
              whiteSpace="pre-wrap"
              wordBreak="break-word"
              fontFamily="mono"
              color={scoresText === "None mentioned" ? "gray.500" : "gray.800"}
              fontStyle={scoresText === "None mentioned" ? "italic" : "normal"}
            >
              {scoresText}
            </Box>
          </CopilotSectionPanel>
        ),
      });

      pushPlainSection("report_plain", "Report", ct.report_plain ?? ct.report, {
        alwaysShow: true,
      });

      if (Array.isArray(ct.facts) && ct.facts.length > 0) {
        sections.push({
          id: "facts",
          label: `Facts (${ct.facts.length})`,
          node: (
            <CopilotSectionPanel>
              {ct.facts.map((fact, i) => (
                <Box key={i} mb={2}>
                  <Text fontSize="sm" color="gray.800">
                    {i + 1}.{" "}
                    {fact.text ||
                      (typeof fact === "string" ? fact : JSON.stringify(fact))}
                    {fact.group && (
                      <Badge
                        ml={1}
                        size="sm"
                        colorScheme="gray"
                        fontSize="0.65em"
                      >
                        {fact.group}
                      </Badge>
                    )}
                  </Text>
                </Box>
              ))}
            </CopilotSectionPanel>
          ),
        });
      }

      if (
        ct.soap_notes &&
        typeof ct.soap_notes === "object" &&
        Object.keys(ct.soap_notes).length > 0
      ) {
        const sn = ct.soap_notes;
        if (sn.transcript_only || sn.facts_only || sn.combined) {
          sections.push({
            id: "soap_notes",
            label: "SOAP",
            node: (
              <CopilotSectionPanel>
                <VStack align="stretch" spacing={3}>
                  {sn.transcript_only && (
                    <Box>
                      <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        color="gray.600"
                      >
                        Transcript-based
                      </Text>
                      <Text
                        fontSize="sm"
                        color="gray.800"
                        whiteSpace="pre-wrap"
                      >
                        {sn.transcript_only}
                      </Text>
                    </Box>
                  )}
                  {sn.facts_only && (
                    <Box>
                      <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        color="gray.600"
                      >
                        Facts-based
                      </Text>
                      <Text
                        fontSize="sm"
                        color="gray.800"
                        whiteSpace="pre-wrap"
                      >
                        {sn.facts_only}
                      </Text>
                    </Box>
                  )}
                  {sn.combined && (
                    <Box>
                      <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        color="gray.600"
                      >
                        Combined
                      </Text>
                      <Text
                        fontSize="sm"
                        color="gray.800"
                        whiteSpace="pre-wrap"
                      >
                        {sn.combined}
                      </Text>
                    </Box>
                  )}
                </VStack>
              </CopilotSectionPanel>
            ),
          });
        }
      }
    }

    return (
      <VStack align="stretch" spacing={3} py={2}>
        <HStack flexWrap="wrap" spacing={2} align="flex-start" rowGap={2}>
          {copilotPayload.created_at && (
            <Text fontSize="xs" color="gray.500">
              Saved: {new Date(copilotPayload.created_at).toLocaleString()}
            </Text>
          )}
          {canGenerateOrRegenerateCopilot && (
            <Button
              size="xs"
              variant="outline"
              colorScheme="purple"
              onClick={handleGenerateCopilotTabs}
              isLoading={copilotGenerateLoading}
              loadingText="Regenerating…"
              flexShrink={0}
            >
              Regenerate from transcript
            </Button>
          )}
        </HStack>
        {sections.length === 0 ? (
          <VStack align="stretch" spacing={2}>
            <Text fontSize="sm" color="gray.500" fontStyle="italic">
              Copilot snapshot has no clinical sections to display.
            </Text>
            {!canGenerateOrRegenerateCopilot && (
              <Text fontSize="xs" color="gray.500">
                Add a transcript linked to this audio to regenerate tabs from the
                current transcript text.
              </Text>
            )}
          </VStack>
        ) : (
          <Tabs size="sm" variant="soft-rounded" colorScheme="blue">
            <TabList flexWrap="wrap" gap={1} rowGap={1}>
              {sections.map((s) => (
                <Tab key={s.id} fontWeight="medium">
                  {s.label}
                </Tab>
              ))}
            </TabList>
            <TabPanels>
              {sections.map((s) => (
                <TabPanel key={s.id} px={0} pt={3}>
                  {s.node}
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        )}
      </VStack>
    );
  };

  const renderPlaybackTabs = (audioFile) => (
    <Tabs colorScheme="blue" variant="enclosed" mt={3} width="100%">
      <TabList>
        <Tab fontWeight="semibold">Transcripts</Tab>
        <Tab fontWeight="semibold">Copilot</Tab>
      </TabList>
      <TabPanels>
        <TabPanel px={0}>{renderTranscriptSection(audioFile)}</TabPanel>
        <TabPanel px={0}>{renderCopilotStorageTab()}</TabPanel>
      </TabPanels>
    </Tabs>
  );

  // Render delete audio button
  const renderDeleteAudioButton = () => {
    if (!onDeleteAudio) return null;
    return (
      <HStack justify="flex-end" mt={3}>
        <Button
          size="sm"
          colorScheme="red"
          variant="outline"
          leftIcon={<DeleteIcon />}
          onClick={() => {
            if (
              window.confirm(
                "Are you sure you want to delete this audio file? This will also delete its transcription backups.",
              )
            ) {
              onDeleteAudio(audio);
            }
          }}
        >
          Delete Audio
        </Button>
      </HStack>
    );
  };

  // If we have multiple audio files, show selection interface
  if (allAudios && allAudios.length > 1) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent maxWidth="60%" maxHeight="80vh">
          <ModalHeader>Select Audio File</ModalHeader>
          <ModalCloseButton />
          <ModalBody maxHeight="60vh" overflowY="auto">
            <VStack spacing={4} align="stretch">
              <Text fontSize="lg" fontWeight="bold" mb={2}>
                Available Audio Files ({allAudios.length}):
              </Text>

              {allAudios.map((audioFile, index) => (
                <Box
                  key={index}
                  p={4}
                  borderWidth={1}
                  borderRadius="md"
                  borderColor={
                    selectedAudio?.url === audioFile.url
                      ? "blue.500"
                      : "gray.200"
                  }
                  bg={
                    selectedAudio?.url === audioFile.url ? "blue.50" : "white"
                  }
                  cursor="pointer"
                  _hover={{
                    bg:
                      selectedAudio?.url === audioFile.url
                        ? "blue.100"
                        : "gray.50",
                  }}
                  onClick={() => setSelectedAudio(audioFile)}
                >
                  <HStack justify="space-between">
                    <VStack align="start" spacing={1}>
                      <HStack spacing={2}>
                        <Text fontWeight="bold">
                          {recordingLabel(audioFile, index + 1)}
                        </Text>
                        {audioFile.source === "uploaded" && (
                          <Badge colorScheme="purple" fontSize="0.65em">
                            UPLOADED
                          </Badge>
                        )}
                      </HStack>
                      <Text fontSize="sm" color="gray.600">
                        Duration:{" "}
                        {audioFile.duration
                          ? `${Math.floor(
                              audioFile.duration / 60,
                            )}:${Math.round(audioFile.duration % 60)
                              .toString()
                              .padStart(2, "0")}`
                          : "Unknown"}
                      </Text>
                    </VStack>
                    {selectedAudio?.url === audioFile.url && (
                      <Badge colorScheme="blue">Playing</Badge>
                    )}
                  </HStack>
                </Box>
              ))}

              {selectedAudio && (
                <Box mt={4}>
                  <AudioPlayer
                    audioUrl={selectedAudio.url}
                    audio_duration={selectedAudio.duration}
                    patientId={selectedAudio.patient_id}
                    audioSavedAt={
                      selectedAudio.uploaded_at ||
                      selectedAudio.recording_date ||
                      selectedAudio.timestamp
                    }
                    audioDisplayName={pickAudioDisplayName(
                      selectedAudio,
                      (() => {
                        if (!Array.isArray(allAudios)) return 1;
                        const ix = allAudios.findIndex(
                          (a) => a?.url === selectedAudio?.url,
                        );
                        return ix >= 0 ? ix + 1 : 1;
                      })(),
                    )}
                  />
                  {renderDeleteAudioButton()}
                  {renderPlaybackTabs(selectedAudio)}
                </Box>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  // Single audio file - show the original interface
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent maxWidth="50%" maxHeight="none">
        <ModalHeader>
          <HStack spacing={2}>
            <span>Audio Playback</span>
            {audio?.source === "uploaded" && (
              <Badge colorScheme="purple">UPLOADED</Badge>
            )}
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <Box
            flex="1"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <AudioPlayer
              audioUrl={audio?.url}
              audio_duration={audio?.duration}
              patientId={audio?.patient_id}
              audioSavedAt={
                audio?.uploaded_at || audio?.recording_date || audio?.timestamp
              }
              audioDisplayName={pickAudioDisplayName(audio)}
            />
          </Box>
          <Box width="100%" textAlign="center" mt={4}>
            <Text fontSize="sm" color="gray.600" fontWeight="bold">
              {(() => {
                const dateProperty =
                  audio?.uploaded_at ||
                  audio?.recording_timestamp ||
                  audio?.recording_date ||
                  audio?.date ||
                  audio?.created_at;
                const label =
                  audio?.source === "uploaded" ? "Uploaded" : "Recorded";
                return dateProperty
                  ? `${label}: ${new Date(dateProperty).toLocaleString()}`
                  : "Date: Unknown";
              })()}
            </Text>
          </Box>
          {renderDeleteAudioButton()}
          {audio ? renderPlaybackTabs(audio) : null}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AudioPlayerModal;
